import {createSlice, PayloadAction, createSelector} from '@reduxjs/toolkit';

import {isServer} from 'coreUtils/utils';

import type {RootState} from '../../types';
import type {ProductsState} from './types';

const productSelector = (state: RootState) => state.products;

export const productsSlice = createSlice({
	name: 'products',
	initialState: {},
	reducers: {
		receiveProduct: (state: ProductsState, action: PayloadAction<any>) => {
			const object = {
				payload: {}
			};

			if (isServer || action.payload?.type?.variant || action.payload?.type?.set) {
				action.payload.ready = true;
			}

			object.payload[action.payload.id] = action.payload;
			(productsSlice as any).reducers.receiveProducts(state, object);
		},
		receiveProducts: (state: ProductsState, action: PayloadAction<any>) => {
			const now = Date.now();

			Object.values(action.payload as any).forEach((p: any) => {
				p.updated = now;

				if (isServer || p?.type?.variant || p?.type?.set) {
					p.ready = true;
				}
			});
			Object.assign(state, action.payload);
		},
		markReady: (state: ProductsState, action: PayloadAction<any>) => {
			action.payload.forEach((id) => {
				const now = Date.now();
				const p = state[id];

				if (p) {
					p.updated = now;
					p.ready = true;
				}
			});
		},
		cleanupProducts: (state: ProductsState) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			state = {};
		},
		markToBeRefreshed: (state: ProductsState) => {
			const now = Date.now();

			for (const product of Object.values(state)) {
				product.toBeRefreshed = true;
				product.updated = now;
			}
		}
	}
});

const enrichData = (allProducts, product) => {
	let updated = product?.updated;

	if (product?.type?.variationGroup && product?.variants) {
		const fullVariants = [];

		product.variants.forEach((vi) => {
			const variant = allProducts[vi.productId];

			if (variant) {
				if (variant.type.variant) {
					fullVariants.push(variant);
				} else {
					fullVariants.push({ // it could be a placeholder, so we force its type
						...variant, type: {
							...variant.type,
							variant: true
						}
					});
				}

				updated = Math.max(updated, variant.updated);
			}
		});

		return {...product, fullVariants, updated};
	} else {
		return product;
	}
};

const selectPID = (state, pid) => pid;
export const {receiveProduct, receiveProducts, cleanupProducts, markToBeRefreshed, markReady} = productsSlice.actions;
export const selectProducts = createSelector(productSelector, (products) => products);
export const selectProductsByIds = createSelector([selectProducts, selectPID], (products, ids) =>
	ids.map((i) => (enrichData(products, products[i])))
);
export const selectProduct = createSelector([selectProducts, selectPID], (products, pid) =>
	enrichData(products, products[pid])
);
export const selectProductVariation = selectProduct;

export const productEqualityCheck = (prevProduct, product) => {
	if (typeof product !== typeof prevProduct) {
		return false;
	} else if (product?.type?.placeholder !== prevProduct?.type?.placeholder) {
		return false;
	} else if (product?.id !== prevProduct?.id) {
		return false;
	} else {
		return product?.updated === prevProduct?.updated;
	}
};

export const productsEqualityCheck = (prev = [], next = []) => {
	return next.reduce((isEqual, product, index) => {
		const prevProduct = prev[index];

		if (!isEqual) {
			return isEqual;
		} else {
			return productEqualityCheck(prevProduct, product);
		}
	}, prev.length === next.length);
};

export default productsSlice.reducer;
